import axios from "axios";
import { Check, ChevronDown, ChevronsUp, Copy, Crosshair, Download, Pause, Play, RotateCcw, Share2, Shield, Skull, Sword, Trophy, Volume2, VolumeX, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "./GameFullscreenButton";
import { toast } from "./ui/sonner";
import { getLinkedTag, getTubeCount, spendTube } from "../utils/rewards";
import { getCachedUser } from "../utils/auth";
import { loadGameAssets } from "./wasteland/assets";
import { createAudio } from "./wasteland/audio";
import { API, BOSS_WORM_HP, BOSS_WORMS, dailyKey, DELIVERY_MAX, DELIVERY_TARGET, DEVOURED_BASELINE, fetchServerArsenal, freshState, H, isBossWave, loadArsenal, MELEE_WEAPONS, mergeArsenal, pushServerArsenal, RANGED_WEAPONS, saveArsenal, seedFromKey, SUSPEND_FIELDS, SUSPEND_KEY, SUSPEND_TTL, W, WORM_TIERS } from "./wasteland/constants";
import { createCombat, stepWorld } from "./wasteland/engine";
import { renderFrame } from "./wasteland/render";
import { buildReplayCard } from "./wasteland/replayCard";
import { WastelandTrophies } from "./WastelandTrophies";

/* live HUD — the whole score block sits in the black bar above the game screen,
   updated imperatively from the run loop so React never re-renders per frame */
const setHudText = (el, text) => {
  if (el && el.textContent !== text) el.textContent = text;
};
const updateWrunHud = (g, hud) => {
  if (!hud.root) return;
  const total = Math.floor(g.distance) + g.credits * 25;
  setHudText(hud.score, String(total).padStart(6, "0"));
  setHudText(hud.credits, `CR ×${g.credits}`);
  const bossWave = isBossWave(g);
  const platingMax = bossWave ? 8 : 5;
  const pltKey = `${platingMax}:${g.wormHp}`;
  if (hud.plt && hud.plt.dataset.k !== pltKey) {
    hud.plt.dataset.k = pltKey;
    hud.plt.innerHTML = "";
    for (let i = 0; i < platingMax; i += 1) {
      const pip = document.createElement("i");
      if (i < g.wormHp) pip.className = "on";
      hud.plt.appendChild(pip);
    }
  }
  setHudText(hud.wormLabel, `WORM PROXIMITY // ${bossWave ? "APEX DEVOURER — ENRAGED" : `${WORM_TIERS[g.tier].name} CLASS`}`);
  if (hud.wormBar) {
    hud.wormBar.style.transform = `scaleX(${Math.max(0, Math.min(1, 1 - (g.worm - 25) / 205)).toFixed(3)})`;
    hud.wormBar.classList.toggle("is-danger", g.worm < 100);
  }
  setHudText(hud.burstText, `BURST CELLS — NEXT IN ${20 - (g.jumpTotal % 20)} JUMPS${g.boost > 0 ? ` · DRIVE ${Math.ceil(g.boost / 60)}S` : ""}`);
  const boostKey = `${g.boosts}:${g.boost > 0}`;
  if (hud.boostPips && hud.boostPips.dataset.k !== boostKey) {
    hud.boostPips.dataset.k = boostKey;
    hud.boostPips.classList.toggle("is-burning", g.boost > 0);
    const pips = hud.boostPips.children;
    for (let i = 0; i < pips.length; i += 1) pips[i].classList.toggle("on", i < g.boosts);
  }
  setHudText(hud.crows, `CROWS FELLED ×${g.crowKills}${g.tier >= 2 ? " — EMBER FLOCK STIRS" : ""}`);
  setHudText(hud.beasts, `BEASTS FELLED ×${g.beastKills}`);
  const wb = g.wormBoss;
  const shown = Math.min(g.delivery, DELIVERY_MAX);
  const overdrive = g.weaponBoost > 0;
  if (wb) {
    const weakLive = wb.weakWin > 0;
    setHudText(hud.delivery, `DELIVERY ${shown}/${DELIVERY_MAX} — !! BOSS WORM // ${BOSS_WORMS[wb.variant].name} · BOSS-LEVEL ARSENAL !!`);
    setHudText(hud.hull, weakLive
      ? `HULL ${Math.max(0, wb.hp).toLocaleString()} / ${BOSS_WORM_HP.toLocaleString()} — !! WEAK POINT EXPOSED · HITS ×2 !!`
      : `HULL ${Math.max(0, wb.hp).toLocaleString()} / ${BOSS_WORM_HP.toLocaleString()} — JUMP THE SHOCKWAVES · HOLD FIRE`);
    if (hud.hull) hud.hull.classList.toggle("is-weak", weakLive);
    if (hud.hullBar) hud.hullBar.style.transform = `scaleX(${Math.max(0, Math.min(1, wb.hp / BOSS_WORM_HP)).toFixed(3)})`;
    if (hud.hullWrap) hud.hullWrap.style.display = "";
    if (hud.deliveryBarWrap) hud.deliveryBarWrap.style.display = "none";
  } else {
    setHudText(hud.delivery, bossWave
      ? `DELIVERY ${shown}/${DELIVERY_MAX} — !! BOSS WAVE // APEX ENRAGED · IT MUST LAND !!`
      : `DELIVERY ${shown}/${DELIVERY_MAX} — BOOMBOX MK-${g.weaponLevel}${overdrive ? ` OVERDRIVE ${Math.ceil(g.weaponBoost / 60)}S` : g.weaponLevel >= 3 ? " MISSILES" : ""}`);
    const roundProgress = Math.max(0, Math.min(1, (total - (shown - 1) * DELIVERY_TARGET) / DELIVERY_TARGET));
    if (hud.deliveryBar) {
      hud.deliveryBar.style.transform = `scaleX(${roundProgress.toFixed(3)})`;
      hud.deliveryBar.classList.toggle("is-bosswave", bossWave);
      hud.deliveryBar.classList.toggle("is-overdrive", !bossWave && overdrive);
    }
    if (hud.deliveryBarWrap) hud.deliveryBarWrap.style.display = "";
    if (hud.hullWrap) hud.hullWrap.style.display = "none";
  }
  hud.root.classList.toggle("is-boss", Boolean(wb) || bossWave);
};

export function WastelandRun() {
  const canvasRef = useRef(null);
  const stateRef = useRef(freshState());
  const phaseRef = useRef("title");
  const spriteRef = useRef(null);
  const wormsRef = useRef([]);
  const bannerRef = useRef(null);
  const sfxRef = useRef({});
  const viewRef = useRef({ scale: 1, camX: 0 });
  const [phase, setPhase] = useState("title");
  const [boostChoice, setBoostChoice] = useState(null);
  const [tubesLeft, setTubesLeft] = useState(() => getTubeCount());
  const [activeBoost, setActiveBoost] = useState(null);
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [score, setScore] = useState(0);
  const [copied, setCopied] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("wasteland-run-muted") === "1");
  // daily challenge — one fixed course per UTC day, shared leaderboard
  const [dailyInfo, setDailyInfo] = useState(null);
  const [dailyMode, setDailyMode] = useState(false);
  const [dailyName, setDailyName] = useState(() => getCachedUser()?.name || "");
  const [dailyLogged, setDailyLogged] = useState(null);
  const [dailyBusy, setDailyBusy] = useState(false);
  const mutedRef = useRef(muted);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const [arsenal, setArsenal] = useState(() => loadArsenal());

  // equip a weapon from the secured arsenal — works on the title screen and mid-run,
  // and syncs to the Titan Tag so the loadout follows the runner across devices
  const equipWeapon = (slot, id) => {
    const current = loadArsenal();
    if (!current.owned.includes(id)) return;
    const next = { ...current, [slot]: id };
    saveArsenal(next);
    setArsenal(next);
    pushServerArsenal(getLinkedTag(), next);
    const g = stateRef.current;
    g.owned = next.owned;
    if (slot === "ranged") g.ranged = id;
    else g.melee = id;
  };

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/wasteland/daily`)
      .then(({ data }) => {
        if (alive) setDailyInfo(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // arsenal sync on load: pull the tag-linked arsenal, union-merge with local, push the union back
  useEffect(() => {
    const tag = getLinkedTag();
    if (!tag) return undefined;
    let cancelled = false;
    fetchServerArsenal(tag)
      .then((server) => {
        if (cancelled) return;
        const merged = mergeArsenal(loadArsenal(), server);
        saveArsenal(merged);
        setArsenal(merged);
        const g = stateRef.current;
        g.owned = merged.owned;
        g.ranged = merged.ranged;
        g.melee = merged.melee;
        pushServerArsenal(tag, merged);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const boostFnRef = useRef(null);
  const combatFnsRef = useRef(null);
  const doloRef = useRef({ run: [], jump: null, fwd: [], back: [], slash: [] });
  const crowRef = useRef([]);
  const beastRef = useRef({});
  const bossRef = useRef([]);
  const hudRef = useRef({});
  const bgRef = useRef(null);
  const [savedRun, setSavedRun] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(SUSPEND_KEY));
      if (raw && raw.g && Date.now() - raw.at < SUSPEND_TTL) return raw;
    } catch {
      /* corrupt save — ignore */
    }
    return null;
  });
  const [cardUrl, setCardUrl] = useState(null);
  const [devoured, setDevoured] = useState(null);
  const [best, setBest] = useState(() => Number(localStorage.getItem("wasteland-run-best") || 0));

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    window.__wrState = stateRef; // debug/test handle — lets automated runs inspect the live world
    loadGameAssets({ doloRef, spriteRef, wormsRef, bannerRef, crowRef, bgRef, stateRef, beastRef, bossRef });

    // site-wide devoured tally
    let alive = true;
    axios
      .get(`${API}/worm/kills`)
      .then(({ data }) => {
        if (alive && typeof data.kills === "number") setDevoured(DEVOURED_BASELINE + data.kills);
      })
      .catch(() => {});
    const { playSfx } = createAudio({ sfxRef, mutedRef, stateRef });

    // responsive camera: narrow screens zoom into a smaller world window
    // so Dolo, the worm, and the HUD stay readable on phones
    const frame = canvas.parentElement;
    let lastCssW = 0;
    const resize = () => {
      const cssW = Math.max(280, frame.clientWidth);
      if (Math.abs(cssW - lastCssW) < 1) return;
      lastCssW = cssW;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const visibleW = Math.min(W, Math.max(520, cssW * 1.55));
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round((cssW * dpr * H) / visibleW);
      viewRef.current = {
        scale: canvas.width / visibleW,
        camX: Math.max(0, Math.min(W - visibleW, 198 - visibleW * 0.35)),
      };
    };
    resize();
    let resizeRaf = 0;
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(resize);
    });
    observer.observe(frame);

    const combat = createCombat({ stateRef, phaseRef, pausedRef, playSfx });
    boostFnRef.current = combat.fireBoost;
    combatFnsRef.current = { fire: combat.fireShot, slash: combat.doSlash, jump: combat.jump };

    const onKey = (event) => {
      if (event.code === "Space" || event.code === "ArrowUp" || event.code === "KeyW") {
        event.preventDefault();
        combat.jump();
      }
      if (event.code === "ShiftLeft" || event.code === "ShiftRight" || event.code === "KeyB") {
        event.preventDefault();
        combat.fireBoost();
      }
      if (event.code === "KeyJ") {
        event.preventDefault();
        combat.fireShot();
      }
      if (event.code === "KeyK") {
        event.preventDefault();
        combat.doSlash();
      }
      if (event.code === "Digit1") equipWeapon("ranged", "boombox");
      if (event.code === "Digit2") equipWeapon("ranged", "machinegun");
      if (event.code === "Digit3") equipWeapon("ranged", "grenades");
      if (event.code === "Digit4") equipWeapon("melee", "sword");
      if (event.code === "Digit5") equipWeapon("melee", "laser");
      if (event.code === "KeyP" || event.code === "Escape") {
        togglePause();
      }
    };
    const onTap = (event) => {
      event.preventDefault();
      combat.jump();
    };
    // phone-call saver: leaving the tab mid-run freezes and suspends the run
    const onHide = () => {
      if (document.hidden && phaseRef.current === "running") {
        if (!pausedRef.current) togglePause();
        suspendRun();
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("pointerdown", onTap);
    window.__wasteland = { stateRef, phaseRef, sfxRef };

    let raf;
    const loop = () => {
      try {
      const g = stateRef.current;
      g.frame += 1;

      if (phaseRef.current === "running" && !pausedRef.current) {
        stepWorld(g, { playSfx, hitWorm: combat.hitWorm, setScore, setBest, phaseRef, setPhase, setActiveBoost, setDevoured, clearSuspend, onArsenalChange: () => setArsenal(loadArsenal()) });
      }

      renderFrame(g, ctx, { canvas, viewRef, phaseRef, pausedRef, bgRef, crowRef, wormsRef, doloRef, spriteRef, beastRef, bossRef });

      updateWrunHud(g, hudRef.current);

      } catch (frameErr) {
        // never let one bad frame freeze the run — keep the loop alive
        console.error("wasteland frame error", frameErr);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    return () => {
      window.cancelAnimationFrame(raf);
      window.cancelAnimationFrame(resizeRaf);
      observer.disconnect();
      window.removeEventListener("keydown", onKey);
      canvas.removeEventListener("pointerdown", onTap);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      alive = false;
      delete window.__wasteland;
    };
  }, []);
  const start = (daily = false) => {
    const dayKey = daily ? dailyKey() : null;
    stateRef.current = freshState(dayKey);
    setDailyMode(Boolean(dayKey));
    setDailyLogged(null);
    if (dayKey) toast.success(`DAILY COURSE ${dayKey} — SAME WASTELAND FOR EVERY RUNNER TODAY`);
    stateRef.current.spriteReady = Boolean(spriteRef.current);
    // loadout: secured arsenal carries into the run
    const loadout = loadArsenal();
    stateRef.current.owned = loadout.owned;
    stateRef.current.ranged = loadout.ranged;
    stateRef.current.melee = loadout.melee;
    setArsenal(loadout);
    // spend a stocked Bio Hack tube for a one-run energy boost
    if (boostChoice && spendTube()) {
      if (boostChoice === "speed") stateRef.current.tubeBoost = "speed";
      if (boostChoice === "shield") {
        stateRef.current.tubeBoost = "shield";
        stateRef.current.tubeShield = 2;
      }
      setActiveBoost(boostChoice);
      setTubesLeft(getTubeCount());
    } else {
      setActiveBoost(null);
    }
    phaseRef.current = "running";
    pausedRef.current = false;
    setPaused(false);
    setPhase("running");
    setCopied(false);
    setCardUrl(null);
    clearSuspend();
  };

  // log a daily-course score — one entry per callsign per day, best kept
  const logDailyScore = async () => {
    const name = dailyName.trim();
    if (name.length < 2) {
      toast.error("CALLSIGN TOO SHORT — 2+ CHARACTERS.");
      return;
    }
    setDailyBusy(true);
    try {
      const { data } = await axios.post(`${API}/wasteland/daily/score`, { name, score });
      setDailyLogged(data);
      toast.success(`DAILY SCORE LOGGED — RANK #${data.rank} TODAY`);
      if (data.streak >= 3) toast.success(`DAILY STREAK ×${data.streak} — FLAME BADGE ON YOUR ROSTER ROW`);
      const board = await axios.get(`${API}/wasteland/daily`);
      setDailyInfo(board.data);
    } catch {
      toast.error("TRANSMISSION FAILED — RETRY.");
    } finally {
      setDailyBusy(false);
    }
  };

  const shareText = () =>
    `SIGNAL INTERCEPTED — I scored ${score} in WASTELAND RUN, outrunning the mutated demonic sand worm. Best signal: ${best}. Think you can survive the wasteland? ${window.location.origin}/wasteland-run`;

  const shareToX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText())}`, "_blank", "noopener");
  };

  const copySignal = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      localStorage.setItem("wasteland-run-muted", next ? "1" : "0");
      return next;
    });
  };

  const togglePause = () => {
    if (phaseRef.current !== "running") return;
    setPaused((prev) => {
      const next = !prev;
      pausedRef.current = next;
      if (next) {
        suspendRun();
      } else {
        clearSuspend();
      }
      return next;
    });
  };

  // anti-jiggle: while a run is live, lock page scroll so repeated thumb taps
  // can't drag the screen upward mid-game
  // anti-jiggle: while a run is live, lock page scroll (html + body + Lenis
  // smooth-scroll) so repeated thumb taps can't drag the screen mid-game
  useEffect(() => {
    const locked = phase === "running";
    document.body.classList.toggle("wr-playing", locked);
    document.documentElement.classList.toggle("wr-playing", locked);
    const lenis = window.__lenis;
    if (locked) {
      /* the START RUN tap can focus-scroll the page first — snap back to the
         top so the fixed frame and docked keys align with a clean origin */
      if (lenis) lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
      if (lenis) lenis.stop();
    }
    return () => {
      document.body.classList.remove("wr-playing");
      document.documentElement.classList.remove("wr-playing");
      if (lenis) lenis.start();
    };
  }, [phase]);

  const suspendRun = () => {
    if (phaseRef.current !== "running") return;
    const snapshot = {};
    SUSPEND_FIELDS.forEach((key) => {
      snapshot[key] = stateRef.current[key];
    });
    const payload = { at: Date.now(), g: snapshot };
    localStorage.setItem(SUSPEND_KEY, JSON.stringify(payload));
    setSavedRun(payload);
  };

  const clearSuspend = () => {
    localStorage.removeItem(SUSPEND_KEY);
    setSavedRun(null);
  };

  const resumeRun = () => {
    if (!savedRun || Date.now() - savedRun.at >= SUSPEND_TTL) {
      clearSuspend();
      return;
    }
    stateRef.current = { ...freshState(), ...savedRun.g, spriteReady: Boolean(spriteRef.current) };
    phaseRef.current = "running";
    // come back frozen behind the veil — the runner readies up, then taps to continue
    pausedRef.current = true;
    setPaused(true);
    setPhase("running");
    setCopied(false);
    clearSuspend();
  };

  // shareable 1200x630 field report, drawn on an offscreen canvas at game over or route clear
  useEffect(() => {
    if (phase === "over" || phase === "complete") setCardUrl(buildReplayCard(stateRef.current, bannerRef.current, best));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  const boostPicker = (
    <div className="tube-boost" data-testid="tube-boost-picker">
      <span>BIO HACK TUBES: {tubesLeft} — BURN ONE FOR THIS RUN</span>
      <div className="tube-boost-row">
        <button
          type="button"
          className={`tube-boost-option ${boostChoice === "speed" ? "is-armed" : ""}`}
          onClick={() => setBoostChoice(boostChoice === "speed" ? null : "speed")}
          data-testid="tube-boost-speed"
        >
          <Zap size={13} /> SPEED SURGE
        </button>
        <button
          type="button"
          className={`tube-boost-option ${boostChoice === "shield" ? "is-armed" : ""}`}
          onClick={() => setBoostChoice(boostChoice === "shield" ? null : "shield")}
          data-testid="tube-boost-shield"
        >
          <Shield size={13} /> SHIELD ×2
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="game-frame wrun-frame" data-testid="wasteland-run-game">
      <GameFullscreenButton testid="wasteland-run-fullscreen" />
      <div className="wrun-hud" ref={(el) => { hudRef.current.root = el; }} data-testid="wasteland-hud">
        <div className="wrun-hud-cell wrun-hud-score">
          <span className="wrun-hud-label">SCORE</span>
          <b ref={(el) => { hudRef.current.score = el; }} data-testid="wasteland-hud-score">000000</b>
          <span className="wrun-hud-cr" ref={(el) => { hudRef.current.credits = el; }} data-testid="wasteland-hud-credits">CR ×0</span>
          <span className="wrun-hud-plt"><em>PLT</em><span className="wrun-hud-pips" ref={(el) => { hudRef.current.plt = el; }} data-testid="wasteland-hud-plt" /></span>
        </div>
        <div className="wrun-hud-cell">
          <span className="wrun-hud-label" ref={(el) => { hudRef.current.wormLabel = el; }} data-testid="wasteland-hud-worm-label">WORM PROXIMITY</span>
          <span className="wrun-hud-bar"><i ref={(el) => { hudRef.current.wormBar = el; }} data-testid="wasteland-hud-worm-bar" /></span>
        </div>
        <div className="wrun-hud-cell">
          <span className="wrun-hud-label" ref={(el) => { hudRef.current.burstText = el; }} data-testid="wasteland-hud-burst">BURST CELLS</span>
          <span className="wrun-hud-cells" ref={(el) => { hudRef.current.boostPips = el; }} data-testid="wasteland-hud-cells"><i /><i /><i /></span>
        </div>
        <div className="wrun-hud-cell wrun-hud-kills">
          <span ref={(el) => { hudRef.current.crows = el; }} data-testid="wasteland-hud-crows">CROWS FELLED ×0</span>
          <span ref={(el) => { hudRef.current.beasts = el; }} data-testid="wasteland-hud-beasts">BEASTS FELLED ×0</span>
        </div>
        <div className="wrun-hud-cell wrun-hud-delivery">
          <span className="wrun-hud-label is-delivery" ref={(el) => { hudRef.current.delivery = el; }} data-testid="wasteland-hud-delivery">DELIVERY 1/20</span>
          <span className="wrun-hud-bar is-delivery" ref={(el) => { hudRef.current.deliveryBarWrap = el; }}><i ref={(el) => { hudRef.current.deliveryBar = el; }} data-testid="wasteland-hud-delivery-bar" /></span>
          <span className="wrun-hud-hull" ref={(el) => { hudRef.current.hullWrap = el; }} style={{ display: "none" }}>
            <span className="wrun-hud-bar is-hull"><i ref={(el) => { hudRef.current.hullBar = el; }} data-testid="wasteland-hud-hull-bar" /></span>
            <span className="wrun-hud-hull-text" ref={(el) => { hudRef.current.hull = el; }} data-testid="wasteland-hud-hull">HULL</span>
          </span>
        </div>
      </div>
      <canvas ref={canvasRef} width={W} height={H} data-testid="game-canvas" aria-label="Wasteland Run game canvas" />
      {phase === "running" && (
      <>
      {activeBoost ? (
        <div className="tube-hud-chip" data-testid="tube-hud-chip">
          <Zap size={11} /> BIO HACK — {activeBoost === "speed" ? "SPEED SURGE" : "SHIELD ARMED"}
        </div>
      ) : null}
      <button
        type="button"
        className="game-mute game-pause"
        onPointerDown={(e) => e.preventDefault()}
        onClick={togglePause}
        data-testid="game-pause-button"
        aria-label={paused ? "Resume run" : "Pause run"}
      >
        {paused ? <Play size={15} /> : <Pause size={15} />}
      </button>
      <button
        type="button"
        className="game-mute game-boost"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => boostFnRef.current?.()}
        data-testid="game-boost-button"
        aria-label="Trigger speed burst"
      >
        <Zap size={15} />
      </button>
      <button
        type="button"
        className="game-mute game-fire"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => combatFnsRef.current?.fire()}
        data-testid="game-fire-button"
        aria-label="Fire boombox blast"
      >
        <Crosshair size={15} />
      </button>
      <button
        type="button"
        className="game-mute game-slash"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => combatFnsRef.current?.slash()}
        data-testid="game-slash-button"
        aria-label="Sword slash"
      >
        <Sword size={15} />
      </button>
      <button
        type="button"
        className="game-mute"
        onPointerDown={(e) => e.preventDefault()}
        onClick={toggleMute}
        data-testid="game-mute-button"
        aria-label={muted ? "Unmute game sound" : "Mute game sound"}
      >
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
      </>
      )}
      {phase === "title" ? (
        <div className="game-overlay game-title-overlay" data-testid="game-title-screen">
          <span>BLACK-OPS NET ARCADE // TRAINING SIM 07</span>
          <img
            src="/assets/game-cover.jpg"
            alt="Dolo's Wasteland Run key art"
            className="game-title-art"
            data-testid="game-title-art"
          />
          <h2>WASTELAND RUN</h2>
          <p>Outrun the mutated demonic sand worm. Jump the debris. Hoard the credits. Survive long enough and it evolves.</p>
          <div className="game-tips" data-testid="game-tips">
            <small>SPACE / TAP — JUMP (DOUBLE JUMP ARMED)</small>
            <small>SHIFT / BOLT — SPEED BURST · EVERY 20 JUMPS CHARGES A CELL (MAX 3)</small>
            <small>GOLDEN CELLS DROP RARELY ABOVE THE CREDIT ARCS — GRAB THEM FOR A FREE CHARGE</small>
            <small>J — BOOMBOX BLAST · K — SWORD SLASH · SHATTER THE WORM'S PLATING FOR BOUNTY</small>
            <small>THE BOOMBOX UPGRADES EVERY 10,000 PTS — HARDER HITS, LARGER FIREBALLS, FASTER FIRE, MISSILES FROM MK-3</small>
            <small>EVERY 30,000 PTS COMPLETES A DELIVERY — LOG 20 TO CLEAR THE ROUTE AND BEAT THE GAME</small>
            <small>EVERY 5TH DELIVERY IS A BOSS WAVE — AND EVERY OTHER ROUND A NAMED BOSS WORM DUEL · 10,000 HULL · BOSS-LEVEL ARSENAL ONLINE</small>
          </div>
          {tubesLeft > 0 ? boostPicker : null}
          <div className="game-arsenal" data-testid="game-arsenal">
            <span className="game-arsenal-title">ARSENAL — CRIMSON CASES DROP MID-RUN · SECURE ONCE, PLAY ANYTIME</span>
            <div className="game-arsenal-group">
              <span>J · RANGED</span>
              {RANGED_WEAPONS.map((w, i) => {
                const isOwned = arsenal.owned.includes(w.id);
                return (
                  <button
                    key={w.id}
                    type="button"
                    className={`game-arsenal-btn ${arsenal.ranged === w.id ? "is-on" : ""}`}
                    disabled={!isOwned}
                    onClick={() => equipWeapon("ranged", w.id)}
                    data-testid={`arsenal-ranged-${w.id}`}
                    title={w.desc}
                  >
                    {i + 1} · {w.name}{isOwned ? "" : " — LOCKED"}
                  </button>
                );
              })}
            </div>
            <div className="game-arsenal-group">
              <span>K · MELEE</span>
              {MELEE_WEAPONS.map((w, i) => {
                const isOwned = arsenal.owned.includes(w.id);
                return (
                  <button
                    key={w.id}
                    type="button"
                    className={`game-arsenal-btn ${arsenal.melee === w.id ? "is-on" : ""}`}
                    disabled={!isOwned}
                    onClick={() => equipWeapon("melee", w.id)}
                    data-testid={`arsenal-melee-${w.id}`}
                    title={w.desc}
                  >
                    {i + 4} · {w.name}{isOwned ? "" : " — LOCKED"}
                  </button>
                );
              })}
            </div>
          </div>
          <button type="button" className="button button-primary" onClick={start} data-testid="game-start-button">
            <Play size={15} /> START RUN
          </button>
          {savedRun ? (
            <button type="button" className="game-resume" onClick={resumeRun} data-testid="game-resume-button">
              <RotateCcw size={13} /> RESUME FROZEN RUN — {Math.floor(savedRun.g.distance) + savedRun.g.credits * 25} PTS HELD
            </button>
          ) : null}
        </div>
      ) : null}
      {phase === "over" || phase === "complete" ? (
        <div className="game-overlay game-over-overlay" data-testid={phase === "complete" ? "game-complete-screen" : "game-over-screen"}>
          <span className="game-over-tag">
            {phase === "complete" ? <><Trophy size={14} /> ROUTE COMPLETE — ALL 20 DELIVERIES LOGGED</> : <><Skull size={14} /> THE WORM FEEDS</>}
          </span>
          <h2 data-testid="game-final-score">{score}</h2>
          <p>
            <Trophy size={13} /> BEST SIGNAL: {best}
          </p>
          <small>{phase === "complete" ? "THE BOOMBOX MK-20 SINGS. THE WASTELAND ROUTE IS YOURS." : "THE WASTELAND REMEMBERS EVERY STEP."}</small>
          {dailyMode ? (
            <div className="daily-submit" data-testid="daily-submit">
              <span>DAILY COURSE {stateRef.current.daily} — LOG YOUR SCORE ON THE SHARED BOARD</span>
              {dailyLogged ? (
                <p className="daily-logged" data-testid="daily-logged">
                  <Trophy size={13} /> LOGGED — RANK #{dailyLogged.rank} TODAY ({dailyLogged.score.toLocaleString()} PTS)
                </p>
              ) : (
                <div className="daily-submit-row">
                  <input
                    type="text"
                    value={dailyName}
                    onChange={(e) => setDailyName(e.target.value)}
                    placeholder="YOUR CALLSIGN"
                    maxLength={24}
                    data-testid="daily-callsign-input"
                  />
                  <button type="button" className="button button-primary" onClick={logDailyScore} disabled={dailyBusy} data-testid="daily-log-button">
                    {dailyBusy ? "LOGGING…" : "LOG SCORE"}
                  </button>
                </div>
              )}
            </div>
          ) : null}
          {tubesLeft > 0 ? boostPicker : null}
          <button type="button" className="button button-primary" onClick={() => start(dailyMode)} data-testid="game-restart-button">
            <RotateCcw size={15} /> {phase === "complete" ? "RUN THE ROUTE AGAIN" : "RUN AGAIN"}
          </button>
          {cardUrl ? (
            <div className="game-replay" data-testid="game-replay-card">
              <img src={cardUrl} alt="Wasteland Run field report card" className="game-replay-thumb" data-testid="game-replay-card-image" />
              <a
                href={cardUrl}
                download="wasteland-run-field-report.png"
                className="game-resume"
                data-testid="game-download-card-button"
              >
                <Download size={13} /> SAVE REPLAY CARD
              </a>
            </div>
          ) : null}
          <div className="game-share" data-testid="game-share-card">
            <span>TRANSMIT YOUR SIGNAL</span>
            <div className="game-share-actions">
              <button type="button" onClick={shareToX} data-testid="game-share-x-button">
                <Share2 size={13} /> POST TO X
              </button>
              <button type="button" onClick={copySignal} data-testid="game-share-copy-button">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "SIGNAL COPIED" : "COPY SIGNAL"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
      {phase === "running" ? (
      <div className="game-action-bar" data-testid="game-action-bar">
      <button
        type="button"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => combatFnsRef.current?.fire()}
        data-testid="game-fire-button-mobile"
        aria-label="Fire boombox blast"
      >
        <Crosshair size={16} /> BLAST
      </button>
      <button
        type="button"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => combatFnsRef.current?.slash()}
        data-testid="game-slash-button-mobile"
        aria-label="Sword slash"
      >
        <Sword size={16} /> SLASH
      </button>
      <button
        type="button"
        className="is-boost"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => boostFnRef.current?.()}
        data-testid="game-boost-button-mobile"
        aria-label="Trigger speed burst"
      >
        <Zap size={16} /> BURST
      </button>
      <button
        type="button"
        className="is-jump"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => combatFnsRef.current?.jump()}
        data-testid="game-jump-button-mobile"
        aria-label="Jump"
      >
        <ChevronsUp size={16} /> JUMP
      </button>
      </div>
      ) : null}
      {phase === "running" ? (
      <div className="game-weapon-strip" data-testid="game-weapon-strip">
        {RANGED_WEAPONS.filter((w) => arsenal.owned.includes(w.id)).map((w, i) => (
          <button
            key={w.id}
            type="button"
            className={arsenal.ranged === w.id ? "is-on" : ""}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => equipWeapon("ranged", w.id)}
            data-testid={`strip-ranged-${w.id}`}
            title={w.desc}
          >
            J{i + 1} {w.name}
          </button>
        ))}
        {MELEE_WEAPONS.filter((w) => arsenal.owned.includes(w.id)).map((w, i) => (
          <button
            key={w.id}
            type="button"
            className={arsenal.melee === w.id ? "is-on" : ""}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => equipWeapon("melee", w.id)}
            data-testid={`strip-melee-${w.id}`}
            title={w.desc}
          >
            K{i + 4} {w.name}
          </button>
        ))}
      </div>
      ) : null}
      <p className="game-kills" data-testid="game-kills-counter">
        <Skull size={13} />
        {devoured !== null
          ? `RUNNERS DEVOURED BY THE WORM: ${devoured.toLocaleString()}`
          : "TALLYING THE DEVOURED…"}
      </p>
      <div className="daily-challenge" data-testid="daily-challenge">
        <div className="daily-challenge-head">
          <span className="eyebrow">DAILY CHALLENGE // SHARED COURSE</span>
          <strong>{dailyInfo ? `TODAY'S COURSE — ${dailyInfo.day}` : "TODAY'S COURSE"}</strong>
          {dailyInfo?.yesterday ? (
            <span className="daily-yesterday" data-testid="daily-yesterday">
              <Trophy size={12} /> YESTERDAY'S CHAMPION — {dailyInfo.yesterday.name} · {dailyInfo.yesterday.score.toLocaleString()} PTS
            </span>
          ) : null}
          <p>One fixed wasteland per day — every runner faces the exact same debris, crows, and beasts. Log your score, claim the board.</p>
        </div>
        <div className="daily-board" data-testid="daily-board">
          {dailyInfo?.scores?.length ? (
            dailyInfo.scores.slice(0, 5).map((row, i) => (
              <div className={`daily-board-row${i === 0 ? " is-leader" : ""}`} key={row.name} data-testid={`daily-board-row-${i + 1}`}>
                <span>{["1ST", "2ND", "3RD", "4TH", "5TH"][i]}</span>
                <strong>{row.name}</strong>
                <em>{row.score.toLocaleString()}</em>
              </div>
            ))
          ) : (
            <span className="daily-board-empty">NO SCORES LOGGED TODAY — THE COURSE IS UNCLAIMED</span>
          )}
        </div>
        {phase !== "running" ? (
          <button type="button" className="button button-primary daily-start" onClick={() => start(true)} data-testid="daily-start-button">
            <Zap size={14} /> RUN TODAY'S COURSE
          </button>
        ) : null}
      </div>
      <WastelandTrophies />
      <div className="game-howto" data-testid="run-howto">
        <button
          type="button"
          className={`game-howto-toggle ${howtoOpen ? "is-open" : ""}`}
          onClick={() => setHowtoOpen((v) => !v)}
          data-testid="run-howto-toggle"
        >
          HOW TO PLAY // RUNNER MANUAL
          <ChevronDown size={14} />
        </button>
        {howtoOpen ? (
          <div className="game-howto-grid" data-testid="run-howto-panel">
            {[
              ["JUMP", "SPACE / ↑ / W — or tap the road, or the JUMP key on phones. Double jump armed."],
              ["SPEED BURST", "SHIFT / B or the bolt button. Every 20 jumps charges a cell (max 3) — golden cells drop rarely for a free charge."],
              ["BOOMBOX BLAST", "J or the crosshair button — ranged shots from the hip. Levels up every 10,000 PTS, and rare green supply crates grant a 20-second MK overdrive beyond your level."],
              ["ARSENAL", "Crimson supply cases drop mid-run carrying the LASER SWORD, MACHINE GUN BOOMBOX, and GRENADE CASSETTES — grab one to secure it forever. Equip anytime on the title screen or mid-run: 1/2/3 ranged, 4/5 melee, or the weapon strip."],
              ["SWORD SLASH", "K or the sword button — close combat. Five hits shatter the worm's plating."],
              ["SKY HUNTERS", "Mutated crows dive from the eastern sky — blast them with J or cut them down with K. Five kills earn the CROW HUNTER feat. At night the ember flock stirs: faster, worth double credits."],
              ["DELIVERY ROUTE", "The boombox upgrades every 10,000 PTS — heavier plating damage for quick kills, larger fireballs, faster fire rate, missile chassis from MK-3. Every 30,000 PTS ends the round — delivery logged. Log 20 deliveries to beat the game. Every 5th delivery is a boss wave: the Apex Devourer hunts enraged with thicker plating and hits push it back less until the delivery lands — shatter it for the boss bounty. Every OTHER delivery a named BOSS WORM erupts ahead for a stand-up duel — 10,000 hull, so your arsenal overdrive-jumps to BOSS LEVEL for the fight (rainbow tracers, blue plasma). Jump its rolling shockwaves and hold fire until it bursts. Watch for the pulsing gold WEAK POINT on its face — shots at that height crit for double; most windows sit high, so jump and fire. Felled bosses hang on the Boss Trophy Wall below."],
              ["SURVIVE", "Outrun the worm and hoard credits. The wasteland remembers every step."],
            ].map(([term, desc]) => (
              <div key={term} className="game-howto-row">
                <strong>{term}</strong>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
